/* eslint-disable no-param-reassign */
import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { Grid } from '@mui/system';
import { isEmpty, partition } from 'lodash';
import { useLocation } from 'react-router-dom';
import { MeasurementDateName } from 'api';
import { SYSTEM_COLLAPSE, USER_EXPAND } from 'common/actions/row-groups/types';
import { VALUATIONS_PAGE_KEY, VALUATIONS_PAGE_VALUE } from 'common/constants/notes';
import {
  PDF_EXPORT_QUERY_PARAM_EXPORT_DESCRIPTION_PAGE,
} from 'common/constants/pdfExport';
import { VALUATIONS_SLUG } from 'common/constants/valuations';
import { useFormat } from 'common/hooks';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport';
import { CustomCompactContainer, CustomContainer } from 'components';
import WidgetDrawer from 'components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from 'components/Drawer/widget-drawer/WidgetDrawer.props';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import CompGroupContext, { CompGroupContextType } from 'context/CompGroupContext';
import { useLayoutContext } from 'context/LayoutContext';
import { useExportSignal } from 'features/pdfExport';
import AddGpcDialog from 'pages/Valuations/approaches/guidelinePublicCompanies/AddGpcDialog';
import ExchangeRatesReferenceTable from 'pages/Valuations/approaches/guidelinePublicCompanies/ExchangeRatesReferenceTable/ExchangeRatesReferenceTable';
import {
  MARKET_CURRENCY,
  PdfExportTargets,
  PUBLIC_COMPANIES,
  SORT_ASC,
  SORT_DESC,
} from 'pages/Valuations/util/constants';
import { packDescriptionsForPdf } from 'pages/Valuations/util/packDescriptionsForPdf';
import { sortGpcComparisonForPdfDescriptions } from 'pages/Valuations/util/sortComparisonsForPdfExport';
import { useApproachRegistry } from 'pages/ValuationsAllocation/context/ApproachRegistryContext';
import { useCellOptions, useValuationNotesCache } from 'pages/ValuationsAllocation/hooks';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { useNotes } from 'services/hooks/notes';
import { getNumberValue, getObjectValue, getStringValue } from 'utilities';
import { FIRST_NON_FORWARD_ID, SELECTION, VALUATION_GPC_TABLE_SLUG } from './constants';
import getGPCRowConfig from './gpc/config/getRowConfig';
import gpcRowTransformer from './gpc/config/rowTransformer';
import {
  GPCExtended,
  GPCTableData,
  GuidelinePublicCompaniesProps,
  ValuationApproachGPCExtended,
} from './GuidelinePublicCompanies.props';
import { useCompsActions, useSyncGpcPublicCompRowOrder } from './hooks';
import { PERFORMANCE_METRICS_SPREADSHEET_SLUG } from './PerformanceMetrics/common/constants/performanceMetrics';
import {
  createColumns as performanceMetricsCreateColumns,
  rowConfig as performanceMetricsRowConfig,
  useCustomClasses as usePerformanceMetricsCustomClasses,
} from './PerformanceMetrics/config';
import { ReverseParsedValuationApproachGPC } from './types';
import { isCapitalIqExtraInfoSortKey } from './utils/gpcCapitalIqExtraInfoSort';
import { GPCAttributes } from './utils/types';
import { useCombinedGPCComparisonPerfMetricAttrs } from './utils/useCombinedGPCComparisonPerfMetricAttrs';

const VALUATION_GPC_PUBLIC_COMPANIES_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${VALUATION_GPC_TABLE_SLUG}-container`;
const VALUATION_GPC_PERFORMANCE_METRICS_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${PERFORMANCE_METRICS_SPREADSHEET_SLUG}-container`;
const VALUATION_GPC_EXCHANGE_RATES_CONTAINER_CLASS = `${VALUATIONS_SLUG}-exchange-rates-container`;
const EMPTY_PDF_VALUE = '-';

const GuidelinePublicCompanies = ({ spreadsheets, onChange, workbook }: GuidelinePublicCompaniesProps) => {
  const gpcSpreadsheet = spreadsheets.gpc;
  const { tableData: initialTableData } = gpcSpreadsheet as { tableData: GPCTableData };
  const performanceMetricsSpreadsheet = spreadsheets.performanceMetrics;
  const exchangeRateSheet = spreadsheets.exchangeRates;
  const { financials, approach, companyName, isDisabled } = initialTableData;
  const { valuations_approach_gpc } = getObjectValue(approach);
  const {
    use_multiple_premium_discount: useMultiplePremiumDiscountFromDB,
    is_evaluating_equity_value: isEvaluatingEquityValue,
    use_forward_multiple_discount: useForwardMultipleDiscountFromDB,
  } = getObjectValue(valuations_approach_gpc);

  const prevEvaluatingRef = useRef(isEvaluatingEquityValue);
  const prevMultiplePremiumDiscountRef = useRef(useMultiplePremiumDiscountFromDB);
  const prevForwardMultipleDiscountRef = useRef(useForwardMultipleDiscountFromDB);
  const multiplePremiumDiscountMountedRef = useRef(false);
  const {
    resetConfiguration,
    compGroups,
    setAreThereChanges,
    financialsCurrency,
    financialsPeriods,
    notesInApproach,
    setNotesInApproach,
    marketCurrency,
    marketDate,
    measurementDate,
    gpcAttributes,
    publicCompsByDate,
  } = useContext(ValuationContext);

  const { isExportMode } = useLayoutContext();
  const location = useLocation();
  // Approach Registry: get stable ID for this PC approach
  const { stableIds, setSourceOfChange } = useApproachRegistry();
  const pcStableIdRef = useRef<string | null>(null);
  pcStableIdRef.current = stableIds.getOrCreateId(approach.panelId);

  const hasDifferentCurrencies = useMemo(
    () => financialsCurrency !== marketCurrency,
    [financialsCurrency, marketCurrency]
  );

  const formatProps = useMemo(
    () => ({
      sourceCurrency: financialsCurrency,
      currencyName: 'Financials Currency',
      ...(hasDifferentCurrencies && {
        additionalCurrencies: [
          {
            currency: marketCurrency,
            currencyType: MARKET_CURRENCY,
            currencyName: 'Market Currency',
          },
        ],
      }),
      marketDate,
    }),
    [marketCurrency, financialsCurrency, marketDate, hasDifferentCurrencies]
  );

  const [format, formatDispatch] = useFormat(formatProps);

  const combinedGPCComparisonPerfMetricAttrs = useCombinedGPCComparisonPerfMetricAttrs(gpcAttributes!) as GPCAttributes;

  const [openGpcDialog, setOpenGpcDialog] = useState(false);
  const [collapsibleColumns, setCollapsibleColumns] = useState({
    [FIRST_NON_FORWARD_ID]: true,
  });
  const [hasMultiplePremiumDiscount, setHasMultiplePremiumDiscount] = useState(
    useMultiplePremiumDiscountFromDB || false
  );
  const approachPanelId = approach.panelId;
  const { notes, setNotes, notesHasChanged, setNotesHasChanged, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const { notes: cachedNotes } = useValuationNotesCache({
    cachedNotes: notesInApproach,
    notes,
    notesHasChanged,
    panelId: approachPanelId,
    setCachedNotes: setNotesInApproach,
    setNotes,
    setNotesHasChanged,
  });

  const { customClasses: performanceMetricsCustomClasses } = usePerformanceMetricsCustomClasses();

  const [cellOptions, handleOnChange] = useCellOptions(onChange, approach.valuations_approach_gpc!);

  const [rowGroups, setRowGroups] = useReducer(rowsGroupsReducer, {
    [SELECTION]: hasMultiplePremiumDiscount ? USER_EXPAND : SYSTEM_COLLAPSE,
  });

  // Store companies as they are removed from the table
  const removedGPCs = useRef<{ capitalIQId: string; gpcComparisonsId: number | undefined }[]>([]);

  const [lastSortedColumn, setLastSortedColumn] = useState<{ [key: string]: string } | null>(null);

  const triggerDependenciesRecalculation = useCallback(() => {
    if (pcStableIdRef.current) {
      setSourceOfChange(pcStableIdRef.current);
    }
  }, [setSourceOfChange]);

  useEffect(() => {
    if (prevMultiplePremiumDiscountRef.current !== useMultiplePremiumDiscountFromDB) {
      setHasMultiplePremiumDiscount(useMultiplePremiumDiscountFromDB || false);
      prevMultiplePremiumDiscountRef.current = useMultiplePremiumDiscountFromDB;
    }
  }, [useMultiplePremiumDiscountFromDB]);

  const updateApproach = useCallback(
    (approachGpc: any, tableData: any, updatedSortedColumn = {}, updateDependencies: boolean = false) => {
      const rowConfigParams = {
        companyName,
        approach: approachGpc,
        allCompGroups: compGroups,
        isDisabled,
        marketDate,
        publicCompsByDate: publicCompsByDate ?? undefined,
      };

      // Update GPC Spreadsheet

      gpcSpreadsheet.reset({
        tableData,
        rowConfig: getGPCRowConfig(rowConfigParams),
        columns: gpcRowTransformer({
          approach: approachGpc,
          financials,
          financialsPeriods: financialsPeriods!,
          financialStatementId: tableData.approach.financial_statement,
          sortedColumn: updatedSortedColumn,
          measurementDate: measurementDate as MeasurementDateName,
        }),
      });

      // Update Performance Metrics Spreadsheet

      const performanceMetricsColumns = performanceMetricsCreateColumns({
        valuationsApproachGpc: approachGpc,
        financials,
      });

      performanceMetricsSpreadsheet.reset({
        columns: performanceMetricsColumns,
        rowConfig: performanceMetricsRowConfig({
          companyName: rowConfigParams.companyName,
          customClasses: performanceMetricsCustomClasses,
          valuationsApproachGpc: rowConfigParams.approach,
        }),
        tableData: {
          ...performanceMetricsSpreadsheet.tableData,
          columns: performanceMetricsColumns,
          customData: cellOptions,
          isDisabled,
        },
      });

      resetConfiguration?.();

      if (updateDependencies) {
        triggerDependenciesRecalculation();
      }
    },
    [
      cellOptions,
      companyName,
      compGroups,
      isDisabled,
      gpcSpreadsheet,
      financials,
      financialsPeriods,
      performanceMetricsSpreadsheet,
      measurementDate,
      marketDate,
      performanceMetricsCustomClasses,
      publicCompsByDate,
      resetConfiguration,
      triggerDependenciesRecalculation,
    ]
  );

  const { orderGpcComparisonsForAdd, applyCapitalIqExtraInfoHeaderSort } = useSyncGpcPublicCompRowOrder({
    approachPanelId,
    gpcSpreadsheet,
    lastSortedColumn,
    updateApproach,
    setLastSortedColumn,
  });

  useEffect(() => {
    const updateTableUIAsync = async () => {
      const rowConfigParams = {
        companyName,
        approach: gpcSpreadsheet.tableData.approach.valuations_approach_gpc as ReverseParsedValuationApproachGPC,
        allCompGroups: compGroups,
        isDisabled,
        marketDate,
        publicCompsByDate: publicCompsByDate ?? undefined,
      };
      const updatedColumns = gpcRowTransformer({
        approach: gpcSpreadsheet.tableData.approach.valuations_approach_gpc!,
        financials,
        financialsPeriods: financialsPeriods!,
        financialStatementId: gpcSpreadsheet.tableData.approach.financial_statement!,
        sortedColumn: {},
        measurementDate: measurementDate as MeasurementDateName,
      });
      gpcSpreadsheet.reset({
        rowConfig: getGPCRowConfig(rowConfigParams),
        columns: updatedColumns,
      });
      await workbook.evaluateSpreadsheets(Object.values(spreadsheets));
    };
    if (prevEvaluatingRef.current !== isEvaluatingEquityValue) {
      updateTableUIAsync();
    }
    if (prevForwardMultipleDiscountRef.current !== useForwardMultipleDiscountFromDB) {
      updateTableUIAsync();
    }
    prevEvaluatingRef.current = isEvaluatingEquityValue;
    prevForwardMultipleDiscountRef.current = useForwardMultipleDiscountFromDB;
  }, [
    isEvaluatingEquityValue,
    useForwardMultipleDiscountFromDB,
    financials,
    financialsPeriods,
    publicCompsByDate,
    measurementDate,
    marketDate,
    gpcSpreadsheet,
    companyName,
    isDisabled,
    compGroups,
    workbook,
    spreadsheets,
  ]);

  const {
    onAddComp: onAdd,
    onRemoveComp: onRemove,
    onRemoveMultipleComps: onRemoveMultiple,
    saveCompGroups,
  } = useCompsActions({
    approach,
    combinedGPCComparisonPerfMetricAttrs,
    gpcSpreadsheet,
    updateApproach,
    orderGpcComparisonsForAdd,
    setAreThereChanges,
    removedGPCsRef: removedGPCs,
  });

  const deleteCompGroup = useCallback(
    (compGroupToDelete: number) => {
      const { tableData } = gpcSpreadsheet;

      const { gpc_comparison, valuationapproachgpccompgroup_set, deleted_gpc_comparisons, deleted_comp_groups } =
        getObjectValue(tableData.approach.valuations_approach_gpc);
      const currentDeletedComparisons = deleted_gpc_comparisons || [];
      const currentDeletedCompGroups = deleted_comp_groups || [];

      const comparisonResult = partition(gpc_comparison, item => {
        const compGroupId = item.gpc_approach_comp_group ?? item.comp_group_id;
        return compGroupId === compGroupToDelete;
      });

      const compGroupData = (valuationapproachgpccompgroup_set ?? []).filter(compGroup => {
        const compGroupId = compGroup.id || compGroup.comp_group;
        return compGroupId !== compGroupToDelete;
      });

      const comparisonsToDelete = comparisonResult[0];
      const remainingComparisons = comparisonResult[1];

      const updatedApproach = {
        gpc_comparison: remainingComparisons,
        valuationapproachgpccompgroup_set: compGroupData,
        deleted_comp_groups: [...new Set([...currentDeletedCompGroups, ...[compGroupToDelete]])],
      } as unknown as ValuationApproachGPCExtended;

      if (comparisonsToDelete) {
        updatedApproach.deleted_gpc_comparisons = [...currentDeletedComparisons, ...comparisonsToDelete]
          ?.filter(({ id }: any) => id)
          ?.map(({ id }: any) => id);
      }
      tableData.approach.valuations_approach_gpc = {
        ...tableData.approach.valuations_approach_gpc,
        ...updatedApproach,
      } as unknown as ValuationApproachGPCExtended;
      updateApproach(tableData.approach.valuations_approach_gpc, tableData);
    },
    [gpcSpreadsheet, updateApproach]
  );

  const compGroupContextValue = useMemo(
    () =>
      ({
        saveCompGroups,
        deleteCompGroup,
        tableData: gpcSpreadsheet.tableData,
        approachType: PUBLIC_COMPANIES,
        cellOptions,
        setUseMultiplePremiumDiscount: setHasMultiplePremiumDiscount,
        useMultiplePremiumDiscount: hasMultiplePremiumDiscount,
      }) as unknown as CompGroupContextType,
    [cellOptions, deleteCompGroup, gpcSpreadsheet, saveCompGroups, hasMultiplePremiumDiscount]
  );

  // Multiple Premium Discount
  useEffect(() => {
    setRowGroups({ type: hasMultiplePremiumDiscount ? USER_EXPAND : SYSTEM_COLLAPSE, row: SELECTION });
  }, [hasMultiplePremiumDiscount, setRowGroups]);

  const updateUseMultiplePremiumDiscount = useCallback(async () => {
    const { tableData } = gpcSpreadsheet;
    tableData.approach.valuations_approach_gpc = {
      ...tableData.approach.valuations_approach_gpc,
      use_multiple_premium_discount: hasMultiplePremiumDiscount,
    } as unknown as ValuationApproachGPCExtended;
    updateApproach(tableData.approach.valuations_approach_gpc, tableData);
  }, [gpcSpreadsheet, updateApproach, hasMultiplePremiumDiscount]);

  // Update the Use Multiple Premium Discount when clicking on the button
  useEffect(() => {
    if (!multiplePremiumDiscountMountedRef.current) {
      multiplePremiumDiscountMountedRef.current = true;
      return;
    }
    updateUseMultiplePremiumDiscount();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMultiplePremiumDiscount]);

  // End of Multiple Premium Discount

  const sortGPC = useCallback(
    (key, isStringColumn = false, isDateColumn = false) => {
      const { tableData } = gpcSpreadsheet;

      if (isCapitalIqExtraInfoSortKey(key)) {
        applyCapitalIqExtraInfoHeaderSort(key);
        return;
      }

      const currentSortedColumn = getStringValue(lastSortedColumn?.[key]);
      const currentSortedColumnIsDesc = currentSortedColumn === SORT_DESC;

      const updatedSortedColumn = {
        [key]: currentSortedColumnIsDesc ? SORT_ASC : SORT_DESC,
      };

      const sortedComparison = (tableData.approach.valuations_approach_gpc?.gpc_comparison ?? []).sort((a, b) => {
        const aValue = a?.[key as keyof GPCExtended];
        const bValue = b?.[key as keyof GPCExtended];
        if (aValue && bValue) {
          setLastSortedColumn?.(updatedSortedColumn);

          if (currentSortedColumnIsDesc) {
            return isStringColumn
              ? getStringValue(aValue).localeCompare(getStringValue(bValue))
              : getNumberValue(aValue) - getNumberValue(bValue);
          }

          return isStringColumn
            ? getStringValue(bValue).localeCompare(getStringValue(aValue))
            : getNumberValue(bValue) - getNumberValue(aValue);
        }

        return 0;
      });

      const valuationApproachGPC = {
        ...tableData.approach.valuations_approach_gpc,
        gpc_comparison: sortedComparison.map((comp, index) => ({
          ...comp,
          order: index,
        })),
      };

      tableData.approach.valuations_approach_gpc = valuationApproachGPC as unknown as ValuationApproachGPCExtended;
      updateApproach(valuationApproachGPC, tableData, updatedSortedColumn);
    },
    [applyCapitalIqExtraInfoHeaderSort, gpcSpreadsheet, lastSortedColumn, updateApproach]
  );

  const gpcComparisons = gpcSpreadsheet?.tableData?.approach?.valuations_approach_gpc?.gpc_comparison;
  const gpcDescriptionChunks = useMemo(() => {
    if (!isExportMode) {
      return [];
    }
    const comparisons = sortGpcComparisonForPdfDescriptions(gpcComparisons ?? []);
    return packDescriptionsForPdf(comparisons, c => c.business_description);
  }, [gpcComparisons, isExportMode]);
  const currentDescriptionPageIndex = useMemo(() => {
    if (!isExportMode) {
      return 0;
    }
    const pageFromQuery = new URLSearchParams(location.search).get(PDF_EXPORT_QUERY_PARAM_EXPORT_DESCRIPTION_PAGE);
    const parsedIndex = Number(pageFromQuery ?? '0');
    return Number.isNaN(parsedIndex) ? 0 : parsedIndex;
  }, [location.search, isExportMode]);
  // Clamp the requested chunk to the available range so an out-of-range index
  // (e.g. usePdfPages built N pages from a stale `gpc_comparison.length`)
  // falls back to the last real chunk instead of rendering empty and being
  // reported as `missing` by the orchestrator.
  const safeDescriptionPageIndex =
    gpcDescriptionChunks.length === 0
      ? 0
      : Math.min(Math.max(0, currentDescriptionPageIndex), gpcDescriptionChunks.length - 1);
  const currentGpcDescriptionChunk = gpcDescriptionChunks[safeDescriptionPageIndex] ?? [];

  let gpcExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (!gpcSpreadsheet?.data) {
    gpcExportStatus = 'loading';
  } else if (isEmpty(gpcComparisons)) {
    gpcExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.PUBLIC_COMPS.id,
    status: gpcExportStatus,
    enabled: isExportMode,
  });
  let gpcDescriptionsExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (!gpcSpreadsheet?.data) {
    gpcDescriptionsExportStatus = 'loading';
  } else if (isEmpty(currentGpcDescriptionChunk)) {
    gpcDescriptionsExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.PUBLIC_COMPS_DESCRIPTIONS.id,
    status: gpcDescriptionsExportStatus,
    enabled: isExportMode,
  });
  useExportSignal({
    target: PdfExportTargets.PUBLIC_COMPS_PERFORMANCE_METRICS.id,
    status: performanceMetricsSpreadsheet?.data ? 'ready' : 'loading',
    enabled: isExportMode,
  });

  return (
    <>
      <CustomContainer>
        {/* Public comps */}
        <CustomCompactContainer>
          {/* Public comps */}
          <Grid
            className={VALUATION_GPC_PUBLIC_COMPANIES_CONTAINER_CLASS}
            data-ref={PdfExportTargets.PUBLIC_COMPS.id}
            maxWidth="100%"
            size={{ xs: 12 }}
            style={isExportMode ? EXPORT_MODE_STYLES : {}}>
            <CompGroupContext.Provider value={compGroupContextValue}>
              <ScalarSpreadsheet
                {...gpcSpreadsheet}
                accessSheet
                addSingleRow={() => setOpenGpcDialog(true)}
                rowGroups={rowGroups}
                setRowGroups={setRowGroups}
                allowSortColumn
                collapsibleColumns={collapsibleColumns}
                deleteRowFn={onRemove}
                deleteMultipleRowsFn={onRemoveMultiple}
                format={format}
                formatDispatch={formatDispatch}
                key={gpcSpreadsheet.name}
                // @ts-expect-error: ScalarSpreadsheet is not typed
                onChange={handleOnChange}
                setCollapsibleColumns={setCollapsibleColumns}
                // @ts-expect-error: ScalarSpreadsheet is not typed
                sheet={gpcSpreadsheet}
                sortColumnFn={sortGPC}
                // @ts-expect-error: ScalarSpreadsheet is not typed
                sortedColumn={lastSortedColumn}
                tableTerms={{
                  tableSlug: VALUATION_GPC_TABLE_SLUG,
                }}
                workbook={workbook}
              />
            </CompGroupContext.Provider>
          </Grid>

          {isExportMode && (
            <Grid
              data-ref={PdfExportTargets.PUBLIC_COMPS_DESCRIPTIONS.id}
              maxWidth="100%"
              size={{ xs: 12 }}
              style={EXPORT_MODE_STYLES}
              sx={{ '& .MuiTypography-root': { fontSize: '1.5em', lineHeight: 1.8 } }}>
              {currentGpcDescriptionChunk.map(comparison => (
                <Grid key={`${comparison.cap_iq_id ?? comparison.company_name}-description`} marginBottom={4}>
                  <Typography variant="h3">{comparison.company_name || EMPTY_PDF_VALUE}</Typography>
                  <Typography marginTop={1} variant="body2">
                    {comparison.business_description || EMPTY_PDF_VALUE}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Add comparable company */}
          <Grid maxWidth="100%" size={{ xs: 12 }}>
            <AddGpcDialog
              currentComps={
                gpcSpreadsheet.tableData.approach.valuations_approach_gpc?.gpc_comparison as unknown as GPCExtended[]
              }
              deleteCompGroup={deleteCompGroup}
              disabled={isDisabled}
              onAdd={onAdd}
              openGpcDialog={openGpcDialog}
              saveCompGroups={saveCompGroups}
              setOpenGpcDialog={setOpenGpcDialog}
              source="valuation"
              tableData={initialTableData}
            />
          </Grid>
        </CustomCompactContainer>

        {/* Performance metrics */}
        <Grid
          className={VALUATION_GPC_PERFORMANCE_METRICS_CONTAINER_CLASS}
          data-ref={PdfExportTargets.PUBLIC_COMPS_PERFORMANCE_METRICS.id}
          maxWidth="100%"
          size={{ xs: 12 }}
          // In export mode, size this Grid to the spreadsheet's natural width so
          // html2canvas captures the full table. The Grid is a flex item inside
          // CustomContainer (a Grid container) and MUI's `size` pins its
          // `flex-basis` to the container width, so `maxWidth: none` alone isn't
          // enough — the content (e.g. the volatility 5Y column) overflows the
          // box and gets cropped on the right. Overriding flex-basis/shrink and
          // using `width: max-content` lets the box grow to fit the content.
          style={
            isExportMode
              ? {
                ...EXPORT_MODE_STYLES,
                maxWidth: 'none',
                width: 'max-content',
                flexBasis: 'auto',
                flexGrow: 0,
                flexShrink: 0,
              }
              : {}
          }>
          <ScalarSpreadsheet
            {...performanceMetricsSpreadsheet}
            key={performanceMetricsSpreadsheet.name}
            onChange={onChange}
            sheet={performanceMetricsSpreadsheet}
            workbook={workbook}
            format={format}
            formatDispatch={formatDispatch}
            displayColumnIndicator
            displayRowIndicator
          />
        </Grid>

        {/* Exchange rates */}
        {hasDifferentCurrencies && (
          <Grid className={VALUATION_GPC_EXCHANGE_RATES_CONTAINER_CLASS} maxWidth="100%" size={{ xs: 12 }}>
            <ExchangeRatesReferenceTable
              exchangeRateSheet={exchangeRateSheet}
              format={format}
              formatDispatch={formatDispatch}
              onChange={onChange}
            />
          </Grid>
        )}
      </CustomContainer>

      <WidgetDrawer
        applicationContext={WidgetDrawerApplicationContext.GPC}
        notesProps={{
          isApproach: true,
          isDisabled,
          notes: cachedNotes,
          onAddNote,
          onDeleteNote,
          onUpdateNotes,
          pageType: VALUATIONS_PAGE_VALUE,
          pageTypeId: approach.id,
          pageTypeKey: VALUATIONS_PAGE_KEY,
          setNotes,
        }}
      />
    </>
  );
};

export default GuidelinePublicCompanies;
